import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { connectWidget, type WidgetHost } from "@lawworks/widget-host";
import { useOptionalUser } from "@/components/UserProvider";
import { Button } from "@/components/ui/button";
import {
  callWidgetTool,
  getWidget,
  getWidgetBootstrap,
  widgetProxyUrl,
} from "@/lib/data/requests/widgets";
import type { WidgetReference } from "@/lib/data/schemas/widgets";
import { getSearchActor } from "@/lib/data/react-query/search/scope";
import { VITE_AWS_USER_POOL_CLIENT_ID } from "@/lib/data/static/importEnv";
import { Maximize2, RefreshCw, X } from "lucide-react";
import { Hub } from "aws-amplify/utils";

function ChatWidget({ widget }: { widget: WidgetReference }) {
  const user = useOptionalUser();
  const scope = user?.currentUser.owner
    ? `${user.currentUser.firmId}:${user.currentUser.id}`
    : "";
  const frame = useRef<HTMLIFrameElement>(null);
  const host = useRef<WidgetHost>();
  const container = useRef<HTMLDialogElement>(null);
  const [height, setHeight] = useState(400);
  const [expanded, setExpanded] = useState(false);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  useLayoutEffect(() => {
    const abort = new AbortController();
    let active = true;
    setError(undefined);
    setLoading(true);
    if (!scope) {
      setExpanded(false);
      setLoading(false);
      return;
    }
    const actor = getSearchActor();
    const dispose = () => {
      if (!active) return;
      active = false;
      abort.abort();
      host.current?.close();
      host.current = undefined;
    };
    const closeForIdentity = () => {
      if (!active) return;
      dispose();
      setExpanded(false);
      setLoading(false);
      setError("Your account changed. Reload the widget to continue.");
    };
    const requireActor = () => {
      if (!active) throw new Error("This widget has closed.");
      if (!actor || getSearchActor() !== actor) {
        closeForIdentity();
        throw new Error("Your account changed. Reload the widget to continue.");
      }
    };
    const unlisten = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedOut" || payload.event === "signedIn") {
        closeForIdentity();
      }
    });
    // Amplify Hub is local to one tab. Cognito credentials are shared across
    // tabs, so bind this private frame to the actor that opened it as well.
    const authPrefix = `CognitoIdentityServiceProvider.${VITE_AWS_USER_POOL_CLIENT_ID}.`;
    const storage = (event: StorageEvent) => {
      if (
        event.key === null ||
        (event.key === `${authPrefix}LastAuthUser` &&
          event.newValue !== actor) ||
        (event.key === `${authPrefix}${actor}.accessToken` && !event.newValue)
      )
        closeForIdentity();
    };
    window.addEventListener("storage", storage);
    Promise.all([
      getWidget(widget.widgetId, abort.signal),
      getWidgetBootstrap(abort.signal),
    ])
      .then(([pkg, bootstrap]) => {
        if (!active || !frame.current) return;
        requireActor();
        host.current = connectWidget(frame.current, {
          bootstrap,
          package: pkg,
          proxyUrl: widgetProxyUrl(),
          dark: document.documentElement.classList.contains("dark"),
          call: async (...args) => {
            requireActor();
            const result = await callWidgetTool(...args);
            requireActor();
            return result;
          },
          onSize: setHeight,
          onError: setError,
        });
        setLoading(false);
      })
      .catch(() => {
        if (active) {
          setError(
            "This widget is unavailable or your access changed. Try reloading it.",
          );
          setLoading(false);
        }
      });
    const visible = () => {
      if (document.visibilityState === "visible") {
        try {
          requireActor();
          void host.current?.refresh();
        } catch {
          // Account changes already close and discard the private instance.
        }
      }
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("pageshow", visible);
    const observer = new MutationObserver(() =>
      host.current?.theme(document.documentElement.classList.contains("dark")),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => {
      dispose();
      unlisten();
      window.removeEventListener("storage", storage);
      observer.disconnect();
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("pageshow", visible);
    };
  }, [scope, widget.widgetId, revision]);
  useEffect(() => {
    const dialog = container.current;
    if (!expanded || !dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    dialog.close();
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      dialog.close();
      if (dialog.isConnected) dialog.show();
      previous?.focus();
    };
  }, [expanded]);
  if (!scope)
    return (
      <p className="text-muted-foreground text-sm">
        Widgets are available to firm owners.
      </p>
    );
  return (
    <dialog
      ref={container}
      open
      onCancel={(event) => {
        event.preventDefault();
        setExpanded(false);
      }}
      tabIndex={-1}
      role={expanded ? "dialog" : "region"}
      aria-modal={expanded || undefined}
      aria-label={widget.title}
      className={
        expanded
          ? "bg-background fixed inset-6 m-0 flex h-[calc(100dvh-3rem)] w-[calc(100vw-3rem)] max-w-none flex-col overflow-hidden rounded-xl border p-0 shadow-2xl backdrop:bg-black/40"
          : "bg-background relative m-0 my-3 w-full max-w-none overflow-hidden rounded-xl border p-0"
      }
    >
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="truncate text-sm font-medium">{widget.title}</span>
        <div className="flex shrink-0 gap-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Refresh ${widget.title}`}
            disabled={loading || !!error}
            onClick={() => void host.current?.refresh()}
          >
            <RefreshCw className="size-4" />
            Refresh
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={expanded ? "Collapse widget" : "Expand widget"}
            onClick={() => {
              setExpanded((value) => !value);
            }}
          >
            {expanded ? (
              <X className="size-4" />
            ) : (
              <Maximize2 className="size-4" />
            )}
          </Button>
        </div>
      </div>
      {loading && (
        <p role="status" className="p-4 text-sm">
          Loading widget…
        </p>
      )}
      {error && (
        <div role="alert" className="p-4 text-sm">
          {error}
          <Button
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => {
              setRevision((value) => value + 1);
            }}
          >
            Reload widget
          </Button>
        </div>
      )}
      <iframe
        ref={frame}
        title={widget.title}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        className={
          expanded ? "min-h-0 w-full flex-1 border-0" : "w-full border-0"
        }
        style={{
          height: expanded ? undefined : height,
          display: error ? "none" : undefined,
        }}
      />
    </dialog>
  );
}
export function ChatWidgets({ widgets }: { widgets: WidgetReference[] }) {
  return (
    <>
      {widgets.map((widget) => (
        <ChatWidget key={widget.widgetId} widget={widget} />
      ))}
    </>
  );
}
